import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";

import { BookingWizardDataAtom } from "../../atoms/stores";
import { useInitializeLessonBookingWizard } from "../../hooks/useInitializeLessonBookingWizard/useInitializeLessonBookingWizard";
import { LessonWizardStepContainer } from "../WizardStepContainer/LessonWizardStepContainer";
import { BookingWizardUI } from "./BookingWizardUI";
import { LessonBookingWizardQueryRendererQuery } from "./__generated__/LessonBookingWizardQueryRendererQuery.graphql";

type LessonBookingWizardQueryRendererProps = {
  queryRef: PreloadedQuery<LessonBookingWizardQueryRendererQuery>;
  onClose: () => void;
  lessonTypeId: string;
  canSelectVenue: boolean;
  slots: Array<{
    date: string;
    durationMins: number;
    id: string;
    time: string;
  }>;
  requestedTime?: string | null;
  durationMins?: number;
};

/**
 * Lesson booking wizard
 */
export const LessonBookingWizardQueryRenderer = ({
  slots,
  requestedTime,
  queryRef,
  onClose,
  lessonTypeId,
  canSelectVenue,
  durationMins,
}: LessonBookingWizardQueryRendererProps) => {
  const wizardData = useAtomValue(BookingWizardDataAtom);

  const data = usePreloadedQuery(
    LessonBookingWizardQueryRendererQueryString,
    queryRef
  );

  const { isCompletingBooking, availableCapacity } =
    useInitializeLessonBookingWizard({
      sessionRef: data.session ?? null,
      nodeRef: data.node ?? null,
      lessonRatesRef: data.lessonRates ?? null,
      slots,
      lessonTypeId,
      onClose,
      requestedTime: requestedTime ?? undefined,
      durationMins,
    });

  const shouldNotRender =
    !data.node || !wizardData || (availableCapacity === 1 && !canSelectVenue);

  if (isCompletingBooking || shouldNotRender) {
    return <Loader />;
  }

  return (
    <BookingWizardUI
      currentServerTime={data.currentServerTime}
      uiHeaderRef={data.club ?? null}
      uiFooterRef={data.node ?? null}
      onDismiss={onClose}
      StepContainer={
        data?.node ? (
          <LessonWizardStepContainer
            slots={slots}
            nodeRef={data.node}
            addPlayersRef={data.session?.currentClientProfile ?? null}
            addPlayersPrivateRef={data.session?.currentClientProfile ?? null}
            previousBookingsRef={data.session?.currentClientProfile ?? null}
            onClose={onClose}
          />
        ) : null
      }
    />
  );
};

/**
 * Query for lesson bookings
 */
export const LessonBookingWizardQueryRendererQueryString = graphql`
  query LessonBookingWizardQueryRendererQuery(
    $slotId: ID!
    $membershipTypeId: ID!
    $players: [ID]
    $currentDate: Date!
    $slotType: SlotType
    $instructorId: ID!
    $lessonTypeId: ID!
    $facilityId: ID!
    $clubSlug: String!
    $bookingType: BookingType!
  ) {
    currentServerTime
    club(slug: $clubSlug) {
      ...BookingWizardUIHeaderFragment
    }
    node(id: $slotId) {
      id
      ...AddPlayersStepNode
      ...useInitializeLessonBookingWizardNode
      ...LessonWizardStepContainerNode
      ...BookingWizardUIFooterFragment
    }
    lessonRates: node(id: $lessonTypeId) {
      ...useInitializeLessonBookingWizardLessonType
    }
    session {
      ...useInitializeLessonBookingWizardSession
      currentClientProfile {
        ...AddPlayersStepClientProfile @arguments(players: $players)
        ...AddPlayersPrivateStepClientProfile
        ...PreviousBookingsStepClientProfile
      }
    }
  }
`;
